import * as THREE from 'three';
import { BLOCK_CAPS, type ParticlesBlock } from '@vrlearning/lesson-schema';
import { puff, softDisc } from '../../look/noise.js';
import { buildDrift } from '../particles.js';
import { lavaFountains } from '../eruption.js';
import { smokeColumn } from '../smoke.js';
import type { WorldBuilder } from '../world.js';
import { ashColumn } from './ash-column.js';
import type { StatefulBlock } from './features.js';
import { buildFlame, buildSteam } from './flame.js';

const hex = (colour: string): number => Number.parseInt(colour.slice(1), 16);

/** A built particles block: its stateful settings, and `tick` (the world's frame; tests call it directly). */
export type BuiltParticles = StatefulBlock & { tick(dt: number, elapsed: number): void };

/**
 * The particles block (V2): one of the headset's six kinds, each the effect a code world drew before
 * (decision 2 of PR 0). `level` thins it (gone at 0), `colour`, `opacity` and `glow` follow a state.
 */
export function buildParticlesBlock(
  world: WorldBuilder,
  block: ParticlesBlock,
  name: string,
  /** `ownFrame: false`: the caller ticks it in its own frame (a code world's feature frame). */
  options: { ownFrame?: boolean } = {},
): BuiltParticles {
  let level = block.level;
  const glow = { value: block.glow };
  if (block.glow > 0 && block.kind !== 'aswolk' && block.kind !== 'vlam')
    addGlow(world, block, name, () => glow.value * level);
  switch (block.kind) {
    case 'zweven': {
      const drift = buildDrift({
        count: Math.min(block.count, BLOCK_CAPS.drift),
        volume: {
          radius: block.area,
          low: block.at[1] + block.height[0],
          high: block.at[1] + block.height[1],
        },
        map: world.codeTexture(softDisc(32, 1.6)),
        size: block.size[0],
        colour: hex(block.colour),
        opacity: block.opacity,
        wind: new THREE.Vector3(...block.wind),
        fall: -block.rise,
        seed: block.seed,
        name,
        additive: block.additive,
      });
      // The drift's volume is round the origin; its points stand where `at` says.
      drift.points.position.set(block.at[0], 0, block.at[2]);
      world.addAir(drift.points);
      const material = drift.points.material as THREE.PointsMaterial;
      let opacity = block.opacity;
      const show = (): void => {
        material.opacity = opacity * level;
        drift.points.visible = level > 0.01;
      };
      show();
      const tick = (dt: number, elapsed: number): void => drift.update(dt, elapsed);
      if (options.ownFrame !== false) world.onFrame(tick);
      return {
        tick,
        set(setting, value) {
          if (setting === 'level') level = value as number;
          else if (setting === 'opacity') opacity = value as number;
          else if (setting === 'colour') material.color.set(value);
          else if (setting === 'glow') glow.value = value as number;
          show();
        },
      };
    }
    case 'rook': {
      const column = smokeColumn({
        map: world.codeTexture(puff(64, 5)),
        colour: hex(block.colour),
        opacity: block.opacity,
        name,
        from: new THREE.Vector3(...block.at),
        count: Math.min(block.count, BLOCK_CAPS.puffs),
        height: block.height,
        size: block.size,
        period: block.seconds,
        wander: block.area,
      });
      world.addAir(column.mesh);
      const tick = (_dt: number, elapsed: number): void => column.update(elapsed, level);
      if (options.ownFrame !== false) world.onFeatureFrame(tick);
      return {
        tick,
        set(setting, value) {
          if (setting === 'level') level = value as number;
          else if (setting === 'colour')
            (column.mesh.material.uniforms['uColour']!.value as THREE.Color).set(value);
          else if (setting === 'opacity')
            column.mesh.material.uniforms['uOpacity']!.value = value as number;
          else if (setting === 'glow') glow.value = value as number;
        },
      };
    }
    case 'aswolk': {
      const column = ashColumn(world, {
        at: new THREE.Vector3(...block.at),
        count: Math.min(block.count, BLOCK_CAPS.puffs),
        colour: hex(block.colour),
        opacity: block.opacity,
        seconds: block.seconds,
        base: block.height[0],
        top: block.height[1],
        glow: block.glow,
        glowColour: new THREE.Color(block.glowColour),
        seed: block.seed,
        name,
      });
      world.addAir(column.mesh);
      let was = level;
      let jumped = false;
      const tick = (dt: number, elapsed: number): void => {
        // Thrown up just now: it climbs from the crater (unless the class jumped here and it is whole).
        if (level > 0.01 && was <= 0.01 && !jumped) column.erupt(true);
        jumped = false;
        was = level;
        column.update(dt, elapsed, level);
      };
      if (options.ownFrame !== false) world.onFeatureFrame(tick);
      return {
        tick,
        snap() {
          column.erupt(false);
          jumped = true;
        },
        set(setting, value) {
          if (setting === 'level') level = value as number;
        },
      };
    }
    case 'vonken': {
      const fountains = lavaFountains({
        from: new THREE.Vector3(...block.at),
        count: Math.min(block.count, BLOCK_CAPS.sparks),
        seed: block.seed,
      });
      fountains.points.name = name;
      world.addAir(fountains.points);
      const tick = (dt: number): void => fountains.update(dt, level);
      if (options.ownFrame !== false) world.onFeatureFrame(tick);
      return {
        tick,
        set(setting, value) {
          if (setting === 'level') level = value as number;
        },
      };
    }
    case 'vlam': {
      const flame = buildFlame(world, {
        at: new THREE.Vector3(...block.at),
        radius: block.size[0],
        height: block.size[1],
        colour: new THREE.Color(block.colour),
        opacity: block.opacity,
        glow: block.glow,
        glowColour: new THREE.Color(block.glowColour),
        name,
      });
      const tick = (_dt: number, elapsed: number): void => flame.update(elapsed, level);
      if (options.ownFrame !== false) world.onFeatureFrame(tick);
      return {
        tick,
        set(setting, value) {
          if (setting === 'level') level = value as number;
        },
      };
    }
    case 'stoom': {
      const steam = buildSteam(world, {
        at: new THREE.Vector3(...block.at),
        count: Math.min(block.count, BLOCK_CAPS.puffs),
        size: block.size,
        height: block.height,
        seconds: block.seconds,
        colour: block.colour,
        opacity: block.opacity,
        name,
      });
      const tick = (_dt: number, elapsed: number): void => steam.update(elapsed, level);
      if (options.ownFrame !== false) world.onFrame(tick);
      return {
        tick,
        set(setting, value) {
          if (setting === 'level') level = value as number;
        },
      };
    }
    default:
      return { tick: () => {}, set: () => {} };
  }
}

/** A glow halo at the emitter (ADR-032's halos), its strength a function of the block's state. */
function addGlow(
  world: WorldBuilder,
  block: ParticlesBlock,
  name: string,
  strength: () => number,
): void {
  const holder = new THREE.Object3D();
  holder.name = `${name}-gloed`;
  holder.position.set(...block.at);
  world.root.add(holder);
  world.addHalo({
    on: holder,
    centre: new THREE.Vector3(),
    radius: Math.max(0.05, block.area || block.size[1]),
    colour: new THREE.Color(block.glowColour),
    flicker: (t) => strength() * (0.9 + 0.1 * Math.sin(t * 2.3)),
  });
}
